import { computed, ref, watch } from 'vue'
import api from '@/services/api'
import { useTonAddress, useTonConnectUI } from '@townsquarelabs/ui-vue'
import { useAnalytics } from '@/composables/analytics'
import { beginCell, Cell, toNano, TonClient } from '@ton/ton'
import { nanoid } from 'nanoid'
import { env } from '@/env'
import { player } from '@/composables/game/player'
import { Address } from '@ton/core'
import { JettonMinter } from '@ton-community/assets-sdk'
import Decimal from 'decimal.js'
import { jettonMasters } from '@/composables/game/ecommerce'
import { ECOMMERCE_ITEM_PRICE_TYPE } from '@/types/shared/player-config'

export class TxFailed extends Error {
}
const client = new TonClient({ endpoint: 'https://toncenter.com/api/v2/jsonRPC' })

export const useWalletConnect = () => {
  const rawAddress = useTonAddress(false)
  const userFriendlyAddress = useTonAddress(true)
  const { triggerEvent } = useAnalytics()
  const [tonConnectUI] = useTonConnectUI()
  watch(rawAddress, async rawAddress => {
    if (!rawAddress) return
    if (rawAddress !== player.value.settings.tonWalletAddress) {
      await api.setWalletAddress('ton', rawAddress)
      triggerEvent({ event: 'connect_ton_wallet' })
      player.value.settings.tonWalletAddress = rawAddress
    }
  }, { immediate: true })

  async function doTonPurchaseTransaction({ tonAmount, nonce }: { nonce: string, tonAmount: number }) {
    try {
      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 90,
        messages: [
          {
            address: env.shop.tonPaymentWalletAddress,
            amount: String(Math.round(tonAmount * 1000000000)),
            payload: beginCell()
              .storeUint(0, 32) // op code for generic transfer (can be 0)
              .storeStringRefTail(nonce)
              .endCell()
              .toBoc()
              .toString('base64'),
          },
        ],
      })
    } catch (error) {
      const errorMessage = (error as Error).message
      await api.reportError({
        code: 'ton_payment_failed',
        metadata: JSON.parse(JSON.stringify(error)),
        description: errorMessage,
      })
      if (errorMessage.includes('UserRejectsError') || errorMessage.includes('Transaction was not sent')) {
        throw new TxFailed()
      }
      throw new Error()
    }
  }

  async function doJettonPurchaseTransaction({ address, amount, nonce }: { address: Address, nonce: string, amount: number }) {
    try {
      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 90,
        messages: [
          await createJettonTransferMessage({
            address: address,
            amount: amount,
            comment: nonce,
          }),
        ],
      })
    } catch (error) {
      const errorMessage = (error as Error).message
      await api.reportError({
        code: 'jetton_payment_failed',
        metadata: JSON.parse(JSON.stringify(error)),
        description: errorMessage,
      })
      if (errorMessage.includes('UserRejectsError') || errorMessage.includes('Transaction was not sent')) {
        throw new TxFailed()
      }
      throw new Error()
    }
  }

  async function createJettonTransferMessage({
    address,
    amount,
    comment,
  }: {
    address: Address
    amount: number
    comment: string
  }) {

    const masterContract = client.open(JettonMinter.createFromAddress(address))
    const owner = Address.parse(rawAddress.value)
    const jettonWalletAddr = await masterContract.getWalletAddress(owner)

    const provider = tonConnectUI.wallet?.provider
    if (!provider) throw new Error('No provider on the wallet')

    const forwardPayload = beginCell()
      .storeUint(0, 32) // 0 opcode means we have a comment
      .storeStringTail(comment)
      .endCell()
    const transferBody = beginCell()
      .storeUint(0x0f8a7ea5, 32) // opcode for jetton transfer
      .storeUint(0, 64)
      .storeCoins(new Decimal(amount).mul(address === jettonMasters.prod[ECOMMERCE_ITEM_PRICE_TYPE.USDT] ? 1e6 : 1e9).toNumber())
      .storeAddress(Address.parse(env.shop.tonPaymentWalletAddress))
      .storeAddress(Address.parse(rawAddress.value))
      .storeBit(0)
      .storeCoins(1)
      .storeBit(1)
      .storeRef(forwardPayload)
      .endCell()

    return {
      address: jettonWalletAddr.toString({ urlSafe: true, bounceable: true }),
      amount: toNano('0.05').toString(),
      payload: transferBody
        .toBoc()
        .toString('base64'),
    }
  }

  async function doLingoRaffleTx(contractAddress: string, amount = '10000000', isOffer?: boolean): Promise<string> {
    try {
      const { boc } = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 90, // 60 sec
        messages: [
          {
            address: contractAddress,
            amount: amount,
            payload: beginCell().storeUint(3841462256, 32).endCell().toBoc().toString('base64'),
          },
        ],
      })
      const cell = Cell.fromBase64(boc)
      const buffer = cell.hash()
      return buffer.toString('hex')
    } catch (error) {
      const errorMessage = (error as Error).message
      api.reportError({
        code: 'tx_failed',
        metadata: {},
        description: errorMessage,
      }).then(() => {
      })
      if (errorMessage.includes('UserRejectsError') || errorMessage.includes('Transaction was not sent')) {
        throw new TxFailed(errorMessage)
      }
      if (errorMessage.includes('Context must be an Activity') && isOffer) {
        throw new TxFailed(errorMessage)
      }
      return nanoid(20)
    }
  }

  return {
    userFriendlyAddress,
    rawAddress,
    doLingoRaffleTx: doLingoRaffleTx,
    doTonPurchaseTransaction: doTonPurchaseTransaction,
    doJettonPurchaseTransaction,
    isTonWalletConnected: computed(() => Boolean(rawAddress.value)),
  }
}

export const useDisconnectWallet = () => {
  const [tonConnectUI] = useTonConnectUI()
  const isDisconnecting = ref(false)

  async function disconnectWallet() {
    isDisconnecting.value = true
    try {
      await tonConnectUI.disconnect()
    } finally {
      isDisconnecting.value = false
    }
  }

  return {
    disconnectWallet,
    isDisconnecting,
  }
}
